import { Button, Card, Descriptions, Grid, List, Modal, Row, Space, Table, TableProps, Tag } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';

import { pdf } from '@react-pdf/renderer';
import { IdentityVerificationCaseV1FinanceDetailsTransactionsAdhocTransaction } from '../../../../../../../../generated';
import { PDFViewer } from '../../../../../../../shared/pdf-viewer';
import { DATE_FORMAT } from '../../../../../../../../constants';
import { TransactionPdfData, TransactionsPdf } from './transactions-pdf';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
export interface TransactionsProps {
  data: TransactionData;
}

export interface TransactionData {
  totalReceived: number;
  totalSent: number;
  tableData: IdentityVerificationCaseV1FinanceDetailsTransactionsAdhocTransaction[];
}

export const Transactions: React.FC<TransactionsProps> = (props) => {
  const sortedTransactions = useMemo(
    () => [...props.data.tableData].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [props.data.tableData]
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<IdentityVerificationCaseV1FinanceDetailsTransactionsAdhocTransaction | undefined>(undefined);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  // Effect to generate PDF when the selectedTransaction is updated
  useEffect(() => {
    const generatePdf = async () => {
      if (selectedTransaction) {
        const transactionData: TransactionPdfData = {
          tableData: selectedTransaction
        };
        try {
          // Generate the PDF asynchronously and set the blob state
          const blob = await pdf(<TransactionsPdf data={transactionData} />).toBlob();
          setPdfBlob(blob);
        } catch (error) {
          console.error('Error generating PDF:', error);
        }
      }
    };

    generatePdf();
  }, [selectedTransaction, props.data.totalReceived, props.data.totalSent]);

  const showModal = (transaction: IdentityVerificationCaseV1FinanceDetailsTransactionsAdhocTransaction) => {
    setSelectedTransaction(transaction);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedTransaction(undefined);
    setPdfBlob(null);
  };

  const columns: TableProps<IdentityVerificationCaseV1FinanceDetailsTransactionsAdhocTransaction>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => dayjs(text).utc().format(DATE_FORMAT).toUpperCase()
    },
    {
      title: 'Description',
      dataIndex: 'reason',
      key: 'reason',
      render: (text: string, record: IdentityVerificationCaseV1FinanceDetailsTransactionsAdhocTransaction) => (
        <>{record.amount && record.amount < 0 ? `US$${Math.abs(record.amount)} - Refund ${text}` : `US$${record.amount} - ${text}`}</>
      )
    },
    {
      title: 'Activity',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: 'descend',
      render: (text, record) => (
        <div style={{ display: 'inline-flex', height: '32px' }}>
          <Button className="mr-0.5" type="primary" onClick={() => showModal(record)}>
            View Receipt
          </Button>
          <span className="mr-1.5"></span>
          <Tag style={{ padding: '5px' }}>{`Sent ${dayjs(text).utc().format(DATE_FORMAT).toUpperCase()}`}</Tag>
        </div>
      )
    }
  ];

  const transactionData: TransactionPdfData = {
    tableData: selectedTransaction as IdentityVerificationCaseV1FinanceDetailsTransactionsAdhocTransaction
  };

  useEffect(() => {
    console.log('Selected transaction after state update:', selectedTransaction);
  }, [selectedTransaction]);

  const blobData = pdf(<TransactionsPdf data={transactionData} />).toBlob();
  console.log('blobData: ', blobData);
  const screen = Grid.useBreakpoint();
  const isMobile = screen.xs;

  return (
    <>
      <Row>
        <Descriptions title={undefined} layout="vertical" size="small" column={2}>
          <Descriptions.Item label="Total Received">US${props.data.totalReceived}</Descriptions.Item>
          <Descriptions.Item label="Total Sent">US${props.data.totalSent}</Descriptions.Item>
        </Descriptions>
      </Row>
      <h4>Transactions</h4>
      {!isMobile && (
        <Card bordered={true}>
          <Table
            dataSource={sortedTransactions}
            columns={columns}
            rowKey={(record) => record.transactionReference?.referenceId ?? ''}
            rowHoverable={false}
            pagination={false}
            scroll={{ y: 500 }}
          />
        </Card>
      )}
      {isMobile && (
        <List
          size="small"
          itemLayout="horizontal"
          dataSource={sortedTransactions}
          renderItem={(item) => (
            <List.Item
              key={item.transactionReference?.referenceId}
              actions={[
                <Space direction="vertical" size={'small'} key={item.transactionReference?.referenceId}>
                  <Button onClick={() => showModal(item)}>View Receipt</Button>
                  <Tag >{`Sent ${dayjs(item.createdAt).utc().format(DATE_FORMAT).toUpperCase()}`}</Tag>
                </Space>
              ]}
            >
              <List.Item.Meta description={item.amount && item.amount < 0 ? `US$${Math.abs(item.amount)} - Refund ${item.reason}` : `US$${item.amount} - ${item.reason}`} />
            </List.Item>
          )}
        />
      )}

      <Modal
        title={`Application - ${selectedTransaction?.transactionReference?.referenceId}.pdf`}
        width="fit-content"
        open={isModalVisible}
        closable
        footer={[
          <Button key="close" onClick={handleCancel}>
            Close
          </Button>
        ]}
        onCancel={handleCancel}
        destroyOnClose
      >
        {pdfBlob ? (
          <PDFViewer filename={`Application - ${selectedTransaction?.transactionReference?.referenceId}.pdf`} docId="" blobPromise={Promise.resolve(pdfBlob)}></PDFViewer>
        ) : (
          <p>Loading PDF...</p>
        )}
      </Modal>
    </>
  );
};
